import { Box, Container, Grid2, CircularProgress } from '@mui/material';
import { HomeHeader } from '../../../components/new/headers/HomeHeader';
import { Link, useParams } from 'react-router-dom';
import { printEventDates } from '../../../components/commonFunc';
import { useEffect, useState, useMemo } from 'react';
import { Footer } from '../../../components/footers/Footer';
import HouseOutlinedIcon from '@mui/icons-material/HouseOutlined';
import RoomIcon from '@mui/icons-material/Room';
import PhoneSharpIcon from '@mui/icons-material/PhoneSharp';
import { CalendarDownMenu } from '../../../components/Contents/blogspage/CalendarDownMenu';
import { APILoadingStatus, Map, Marker, useApiLoadingStatus } from '@vis.gl/react-google-maps';
import { geocode, RequestType } from 'react-geocode';
import { backendHost } from '../../../providers/apis/blogApi';
import { getEventDetail } from '../api/api';
import { PageMain } from '../../../components/seo/PageMain';
import { DetailPageHelmet } from '../helper/DetailPageHelmet';
import { getPublicSiteUrl } from '../siteUrl';

function stripHtml(html) {
  if (!html || typeof html !== 'string') return '';
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 320);
}

function getMediaSrc(mediaPath) {
  if (!mediaPath) return '';
  if (typeof mediaPath === 'string' && mediaPath.startsWith('http')) return mediaPath;
  return `${backendHost}${mediaPath}`;
}

function getAddressText(addressData) {
  if (!addressData) return '';
  return [addressData.address, addressData.city, addressData.state, addressData.zip, addressData.country]
    .filter(Boolean)
    .join(', ');
}

function mapEventRow(eventRes) {
  return {
    postId: eventRes.id,
    title: eventRes.Title,
    postMedia: eventRes.Graphic1,
    postType: eventRes.ContentType,
    postDate: eventRes.LastUpdated,
    postName: eventRes.Relevance,
    eventStartDate: eventRes.StartDate,
    eventEndDate: eventRes.EndDate,
    eventUrl: eventRes.EventPath,
    description: eventRes.Description,
    addressData: {
      address: eventRes.Address,
      country: eventRes.Country,
      city: eventRes.City,
      state: eventRes.State,
      zip: eventRes.Zip,
      phone: eventRes.Phone,
    },
  };
}

export function EventDetail() {
  const { eventName } = useParams();
  const [eventData, setEventData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mapReady, setMapReady] = useState(false);
  const [markerLocation, setMarkerLocation] = useState({
    lat: 51.509865,
    lng: -0.118092,
  });

  const siteUrl = getPublicSiteUrl();
  const canonicalUrl = useMemo(() => {
    if (!siteUrl || !eventName) return '';
    return `${siteUrl}/articles/event/${encodeURIComponent(eventName)}`;
  }, [siteUrl, eventName]);

  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    setLoading(true);
    setEventData(null);
    setMapReady(false);
    const name = eventName;
    (async () => {
      if (!name) {
        if (!cancelled) {
          setLoadError('Missing event');
          setLoading(false);
        }
        return;
      }
      try {
        const eventRes = await getEventDetail(name);
        if (cancelled) return;
        const eventObj = mapEventRow(eventRes);
        if (eventObj.addressData) {
          const addressStr = `${eventObj.addressData.address || ''} ${eventObj.addressData.city || ''} ${eventObj.addressData.state || ''} ${eventObj.addressData.zip || ''} ${eventObj.addressData.country || ''}`;
          geocode(RequestType.ADDRESS, addressStr, {
            key: 'AIzaSyAOVYRIgupAurZup5y1PRh8Ismb1A3lLao',
            language: 'en',
            region: 'us',
          })
            .then((response) => {
              if (cancelled) return;
              const { lat, lng } = response.results[0].geometry.location;
              setMarkerLocation({ lat, lng });
            })
            .catch(() => {});
        }
        setEventData(eventObj);
      } catch (e) {
        if (!cancelled) {
          setLoadError('Event not found');
          setEventData(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [eventName]);

  const metaDescription = useMemo(() => {
    if (!eventData) return 'Charlee.ai events';
    const fromDesc = stripHtml(eventData.description);
    if (fromDesc) return fromDesc;
    return `${eventData.title} — ${printEventDates(eventData)}`;
  }, [eventData]);

  const ogImage = useMemo(() => {
    return getMediaSrc(eventData?.postMedia);
  }, [eventData]);

  const pageTitle = eventData
    ? `${eventData.title} | Charlee.ai`
    : 'Event | Charlee.ai';
  const addressText = getAddressText(eventData?.addressData);
  const mapLoadingStatus = useApiLoadingStatus();
  const hasMapLoadError =
    mapLoadingStatus === APILoadingStatus.AUTH_FAILURE ||
    mapLoadingStatus === APILoadingStatus.FAILED;

  const breadcrumbItems = useMemo(() => {
    if (!eventData?.title) return undefined;
    return [
      { name: 'Home', path: '/' },
      { name: 'Events', path: '/articles/events' },
      { name: eventData.title, url: canonicalUrl },
    ];
  }, [eventData, canonicalUrl]);

  return (
    <div className="w-full overflow-hidden bg-[#F3F4F4]">
      {!loading && (
        <DetailPageHelmet
          pageTitle={pageTitle}
          metaDescription={metaDescription}
          canonicalUrl={canonicalUrl}
          ogImage={ogImage}
          ogType="article"
          noIndex={Boolean(loadError)}
          articlePublishedTime={eventData?.postDate || eventData?.eventStartDate}
          breadcrumbItems={breadcrumbItems}
        />
      )}

      <HomeHeader headerColor="#F9F9F9" textColor="#021744" />
      <PageMain className="news-page bg-[#F9F9F9]">
        {loading && (
          <Box className="w-full flex justify-center items-center min-h-[50vh] mt-24">
            <CircularProgress />
          </Box>
        )}
        {!loading && loadError && (
          <Box className="w-full text-center mt-32 px-4">
            <p className="text-lg text-[#021744]">{loadError}</p>
            <Link to="/articles/events" className="text-[#22c0b1] underline mt-4 inline-block">
              Back to events
            </Link>
          </Box>
        )}
        {!loading && eventData && (
          <article className="w-full mt-24">
            <Container maxWidth="lg">
              <Box className="w-full pt-14">
                <Grid2 container spacing={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                  <Grid2 size={12}>
                    <div className="w-full relative aspect-[300/200] rounded-2xl">
                      <img
                        src={getMediaSrc(eventData.postMedia)}
                        alt={eventData.title ? `${eventData.title} — Charlee.ai event` : 'Charlee.ai event'}
                        className="w-full aspect-[300/200] rounded-2xl object-cover"
                        crossOrigin="anonymous"
                      />
                      <Box
                        sx={{
                          background:
                            'linear-gradient(0deg, #22c0b1, #22c0b1, #ffffff00)',
                        }}
                        className="absolute bottom-0 w-full py-9 px-4 rounded-b-2xl"
                      >
                        <h1 className="cursor-pointer text-[42px] leading-[54.6px] text-white">
                          <span className="font-bold">{eventData?.title}</span>
                        </h1>
                        <p className="cursor-pointer text-[42px] leading-[54.6px] text-white">
                          <span className="font-medium">{printEventDates(eventData)}</span>
                        </p>
                      </Box>
                    </div>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <Box className="text-left py-3">
                      <div className="relative w-full h-[300px] mb-7 overflow-hidden rounded-[20px] bg-[#EAF6F4] shadow-sm">
                        <Box
                          className="absolute inset-0 p-6"
                          display="flex"
                          flexDirection="column"
                          justifyContent="center"
                          alignItems="center"
                          textAlign="center"
                          sx={{
                            background:
                              'linear-gradient(135deg, #EAF9F7 0%, #F9F9F9 50%, #E9EEF8 100%)',
                          }}
                        >
                          <Box
                            className="mb-4 h-14 w-14 rounded-full bg-white text-[#22c0b1] shadow-sm"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <RoomIcon />
                          </Box>
                          <p className="text-[18px] leading-[24px] font-semibold text-[#021744]">
                            {hasMapLoadError ? 'Map Unavailable' : 'Event Location'}
                          </p>
                          {hasMapLoadError ? (
                            <p className="mt-3 text-[14px] leading-[22px] text-[#5F6B7A]">
                              We could not load the map right now. Please use the event address
                              below.
                              {addressText ? <span className="block mt-2">{addressText}</span> : null}
                            </p>
                          ) : addressText ? (
                            <p className="mt-3 text-[14px] leading-[22px] text-[#5F6B7A]">
                              {addressText}
                            </p>
                          ) : (
                            <p className="mt-3 text-[14px] leading-[22px] text-[#5F6B7A]">
                              Map details are loading.
                            </p>
                          )}
                        </Box>
                        {!hasMapLoadError && (
                          <Map
                            style={{
                              borderRadius: '20px',
                              height: '100%',
                              opacity: mapReady ? 1 : 0,
                              transition: 'opacity 200ms ease-in-out',
                            }}
                            defaultZoom={13}
                            defaultCenter={markerLocation}
                            gestureHandling={'greedy'}
                            disableDefaultUI
                            onIdle={() => setMapReady(true)}
                            onTilesLoaded={() => setMapReady(true)}
                          >
                            <Marker position={markerLocation} />
                          </Map>
                        )}
                      </div>
                      <CalendarDownMenu eventObj={eventData} />
                    </Box>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }}>
                    <Box className="py-3 text-left">
                      <h3 className="text-[22px] font-semibold py-2">
                        <HouseOutlinedIcon className="mx-2" />
                        WebSite
                      </h3>
                      <p className="pl-2">
                        <a
                          className="break-words underline text-[#22c0b1]"
                          href={eventData.eventUrl}
                          rel="noreferrer"
                        >
                          {eventData.eventUrl}
                        </a>
                      </p>

                      {eventData?.addressData && (
                        <>
                          <h3 className="text-[22px] font-semibold py-2">
                            <RoomIcon className="mx-2" />
                            Address
                          </h3>
                          <h5 className="pl-2 font-semibold py-2">
                            {eventData.addressData.title}
                          </h5>
                          <p className="pl-2 underline">{eventData.addressData.address}</p>
                          <p className="pl-2 underline">
                            {eventData.addressData.city} {eventData.addressData.state}{' '}
                            {eventData.addressData.zip} {eventData.addressData.country}
                          </p>
                          <h3 className="text-[22px] font-semibold py-2">
                            <PhoneSharpIcon className="mx-2" />
                            Phone
                          </h3>
                          <p className="pl-2 underline">{eventData.addressData.phone}</p>
                        </>
                      )}
                    </Box>
                  </Grid2>
                </Grid2>
              </Box>
            </Container>
          </article>
        )}
        {!loading && eventData && (
          <Box className="w-full bg-[#F3F4F4] py-20">
            <Container maxWidth="lg">
              <Box className="w-full">
                <Box
                  className="w-full"
                  display={'flex'}
                  justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
                >
                  <Link to="/articles/events">
                    <button
                      type="button"
                      className="text-[18px] font-medium leading-[24px] rounded-full border border-[#021744] hover:bg-[#021744] hover:text-white px-8 py-4"
                    >
                      View all Events
                    </button>
                  </Link>
                </Box>
              </Box>
            </Container>
          </Box>
        )}
      </PageMain>
      <Footer />
    </div>
  );
}
